// ** react imports
import React from 'react';

// ** react router imports
import { useNavigate } from 'react-router-dom';

// ** redux imports
import { useSelector } from 'react-redux';

// ** validation imports
import * as yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

// ** custom hooks imports
import { useUpdateMobileAppVersion } from '../../../hooks/apis/useMobileVersion';

// ** components imports
import LoadingButton from '../../../components/common/Buttons/LoadingButton';
import NormalButton from '../../../components/common/Buttons/NormalButton';
import NormalInput from '../../../components/common/Inputs/NormalInput';
import SelectInput from '../../../components/common/Inputs/SelectInput';
import SwitchInput from '../../../components/common/Inputs/SwitchInput';
import DynamicKeyValueInput from '../../../partials/DynamicKeyValueInput';

// ** enums imports
import { BUTTON_THEMES } from '../../../enums/common';

const RenderFormSection = props => {
  const { children } = props;

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg mb-6 overflow-hidden">
      <div className="p-4">{children}</div>
    </div>
  );
};

const UpdateMobileAppVersion = () => {
  // hooks
  const navigate = useNavigate();
  const { versionData } = useSelector(state => state.versions);

  // validation schema
  const validationSchema = yup.object().shape({
    platform: yup
      .string()
      .required('Platform is required')
      .oneOf(
        ['ios', 'android'],
        "Invalid platform. Must be 'ios' or 'android'"
      ),
    currentVersion: yup.string().required('Current version is required').trim(),
    minimumSupportedVersion: yup
      .string()
      .required('Minimum supported version is required')
      .trim(),
    maximumSupportedVersion: yup.string().nullable().trim(),
    forceUpdate: yup.boolean().nullable(),
    updateUrl: yup.string().nullable().url('Update URL must be a valid URL'),
    releaseNotes: yup.string().nullable().trim(),
    changelog: yup.string().nullable().trim(),
    whatsNew: yup.string().nullable().trim(),
    isActive: yup.boolean().nullable(),
    isProduction: yup.boolean().nullable(),
    isBeta: yup.boolean().nullable(),
    targetAudience: yup
      .string()
      .nullable()
      .oneOf(
        ['all', 'beta-testers', 'early-access', 'premium-users'],
        'Invalid target audience'
      ),
    rolloutPercentage: yup
      .number()
      .nullable()
      .integer('Rollout percentage must be an integer')
      .min(0, 'Rollout percentage must be between 0 and 100')
      .max(100, 'Rollout percentage must be between 0 and 100'),
    expiryDate: yup
      .string()
      .nullable()
      .matches(
        /^\d{4}-\d{2}-\d{2}$/,
        'Expiry date must be in YYYY-MM-DD format'
      ),
    metadata: yup.object().nullable(),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: 'onTouched',
    defaultValues:
      {
        ...versionData,
        expiryDate: versionData?.expiryDate
          ? new Date(versionData.expiryDate).toISOString().split('T')[0]
          : '',
      } || {},
  });

  const { isUpdateMobileAppVersionLoading, updateMobileAppVersion } =
    useUpdateMobileAppVersion();

  const onSubmit = async data => {
    const updatedVersion = {
      ...versionData,
      ...data,
    };
    const versionId = versionData._id;
    const response = await updateMobileAppVersion(versionId, updatedVersion);

    if (response.data?.success) {
      navigate('/mobile-app-version/list');
    }
  };

  const handleCancel = () => {
    navigate('/mobile-app-version/list');
  };

  // render
  if (!versionData || Object.keys(versionData).length === 0) {
    return (
      <div className="w-full h-[90vh] flex justify-center items-center">
        <div className="flex flex-col justify-center items-center">
          <div className="my-4">
            <h2 className="text-center font-bold text-2xl mx-2">
              Version not selected
            </h2>
            <p className="text-center font-normal text-sm mx-2">
              Please select version
            </p>
          </div>

          <NormalButton
            type="button"
            theme={BUTTON_THEMES.TERTIARY}
            onClick={() => handleCancel()}
          >
            Go Back
          </NormalButton>
        </div>
      </div>
    );
  }

  const renderErrorMessage = error => {
    if (!error) return null;
    return <p className="mt-2 text-xs text-red-500">{error}</p>;
  };

  return (
    <div className="flex">
      <main className="grow">
        <div className="p-8 w-full mx-auto">
          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl text-gray-800 dark:text-gray-100 font-bold">
              Update Application Version
            </h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              Update an existing mobile app version with new details
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 shadow-xs rounded-xl mb-8">
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="p-6 grid grid-cols-1">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <RenderFormSection>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <SelectInput
                          label="Platform"
                          name="platform"
                          options={[
                            { label: 'iOS', value: 'ios' },
                            { label: 'Android', value: 'android' },
                          ]}
                          register={register}
                          errors={errors.platform?.message}
                          required
                        />
                      </div>

                      <div>
                        <NormalInput
                          label="Current Version"
                          type="text"
                          name="currentVersion"
                          register={register}
                          errors={errors.currentVersion?.message}
                          required
                        />
                      </div>

                      <div>
                        <NormalInput
                          label="Minimum Supported Version"
                          type="text"
                          name="minimumSupportedVersion"
                          register={register}
                          errors={errors.minimumSupportedVersion?.message}
                          required
                        />
                      </div>

                      <div>
                        <NormalInput
                          label="Maximum Supported Version"
                          type="text"
                          name="maximumSupportedVersion"
                          register={register}
                          errors={errors.maximumSupportedVersion?.message}
                        />
                      </div>

                      <div>
                        <Controller
                          name="isActive"
                          control={control}
                          render={({ field: { onChange, value } }) => (
                            <SwitchInput
                              label="Is Active"
                              checked={value}
                              onChange={e => onChange(e.target.checked)}
                              errors={errors.isActive?.message}
                            />
                          )}
                        />
                      </div>

                      <div>
                        <Controller
                          name="isProduction"
                          control={control}
                          render={({ field: { onChange, value } }) => (
                            <SwitchInput
                              label="Is Production"
                              checked={value}
                              onChange={e => onChange(e.target.checked)}
                              errors={errors.isProduction?.message}
                            />
                          )}
                        />
                      </div>
                    </div>
                  </RenderFormSection>

                  <RenderFormSection>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <SelectInput
                          label="Target Audience"
                          name="targetAudience"
                          options={[
                            { label: 'All Users', value: 'all' },
                            { label: 'Beta Testers', value: 'beta-testers' },
                            { label: 'Early Access', value: 'early-access' },
                            { label: 'Premium Users', value: 'premium-users' },
                          ]}
                          register={register}
                          errors={errors.targetAudience?.message}
                        />
                      </div>

                      <div>
                        <NormalInput
                          label="Rollout Percentage"
                          type="number"
                          name="rolloutPercentage"
                          register={register}
                          errors={errors.rolloutPercentage?.message}
                          min="0"
                          max="100"
                        />
                      </div>

                      <div>
                        <NormalInput
                          label="Expiry Date"
                          type="date"
                          name="expiryDate"
                          register={register}
                          errors={errors.expiryDate?.message}
                        />
                      </div>

                      <div></div>

                      <div>
                        <Controller
                          name="isBeta"
                          control={control}
                          render={({ field: { onChange, value } }) => (
                            <SwitchInput
                              label="Is Beta"
                              checked={value}
                              onChange={e => onChange(e.target.checked)}
                              errors={errors.isBeta?.message}
                            />
                          )}
                        />
                      </div>

                      <div>
                        <Controller
                          name="forceUpdate"
                          control={control}
                          render={({ field: { onChange, value } }) => (
                            <SwitchInput
                              label="Force Update"
                              checked={value}
                              onChange={e => onChange(e.target.checked)}
                              errors={errors.forceUpdate?.message}
                            />
                          )}
                        />
                      </div>
                    </div>
                  </RenderFormSection>
                </div>

                <RenderFormSection>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-1">
                      <NormalInput
                        label="Update URL"
                        type="url"
                        name="updateUrl"
                        register={register}
                        errors={errors.updateUrl?.message}
                      />
                    </div>

                    <div className="md:col-span-1">
                      <NormalInput
                        label="Changelog"
                        type="text"
                        name="changelog"
                        register={register}
                        errors={errors.changelog?.message}
                      />
                    </div>
                  </div>
                </RenderFormSection>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <RenderFormSection>
                    <div className="space-y-1">
                      <label
                        htmlFor="metadata"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                      >
                        Metadata
                      </label>
                      <Controller
                        name="metadata"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <DynamicKeyValueInput
                            value={value ? value : {}}
                            onChange={onChange}
                            placeholder="Enter metadata as key-value pairs"
                            error={errors.metadata?.message}
                          />
                        )}
                      />
                    </div>
                  </RenderFormSection>

                  <RenderFormSection>
                    <div className="space-y-1">
                      <label
                        htmlFor="metadata"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                      >
                        What&apos;s New
                      </label>
                      <textarea
                        id="whatsNew"
                        name="whatsNew"
                        rows={4}
                        className={`w-full mt-1 form-textarea placeholder:text-sm dark:placeholder:text-gray-400 ${
                          errors.whatsNew
                            ? 'border-red-500 focus:ring-red-500 dark:border-red-600 dark:focus:ring-red-600'
                            : 'border-gray-300 focus:ring-blue-500 dark:border-gray-600 dark:focus:ring-blue-500'
                        } rounded-md shadow-sm`}
                        placeholder="Enter whats new"
                        {...register('whatsNew')}
                      />
                      {renderErrorMessage(errors.whatsNew?.message)}
                    </div>
                  </RenderFormSection>
                </div>

                <div className="flex justify-end gap-3">
                  <NormalButton
                    type="button"
                    theme={BUTTON_THEMES.DANGER}
                    onClick={() => handleCancel()}
                  >
                    Cancel
                  </NormalButton>
                  <LoadingButton
                    type="submit"
                    theme={BUTTON_THEMES.PRIMARY}
                    isLoading={isUpdateMobileAppVersionLoading}
                  >
                    Update Version
                  </LoadingButton>
                </div>
              </div>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
};

export default UpdateMobileAppVersion;
